import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMessagingStore, useConversations, useCurrentConversation } from '../stores/messagingStore';
import { useAuth } from '../contexts/AuthContext';
import { ConversationList, ChatRoom } from '../components/messaging';
import { chatbotService } from '../services/chatbotService';
import type { ChatbotPersonaListItem } from '../types/messaging';
import './MessagesPage.css';

const MessagesPage: React.FC = () => {
    const { conversationId } = useParams<{ conversationId?: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation('messaging');
    const { user, isAuthenticated, isLoading: authLoading } = useAuth();

    const [showSidebar, setShowSidebar] = useState(true);
    const [showPersonaPicker, setShowPersonaPicker] = useState(false);
    const [personas, setPersonas] = useState<ChatbotPersonaListItem[]>([]);
    const [personasLoading, setPersonasLoading] = useState(false);
    const [personasError, setPersonasError] = useState<string | null>(null);
    const [startingPersonaSlug, setStartingPersonaSlug] = useState<string | null>(null);

    const conversations = useConversations();
    const currentConversation = useCurrentConversation();
    const {
        loading,
        error,
        connect,
        disconnect,
        fetchConversations,
        setCurrentConversation,
    } = useMessagingStore();

    // Add layout class to html element for full-height chat layout
    useEffect(() => {
        document.documentElement.classList.add('page-messages');
        return () => document.documentElement.classList.remove('page-messages');
    }, []);

    // Redirect if not authenticated
    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/login', { state: { from: { pathname: '/messages' } } });
        }
    }, [authLoading, isAuthenticated, navigate]);

    // Connect WebSocket and fetch conversations
    useEffect(() => {
        if (isAuthenticated && user) {
            connect();
            fetchConversations();

            return () => {
                disconnect();
            };
        }
    }, [isAuthenticated, user, connect, disconnect, fetchConversations]);

    // Handle conversation selection from URL
    useEffect(() => {
        if (conversationId && conversations.length > 0) {
            const conversation = conversations.find((c) => c.id === conversationId);
            if (conversation) {
                setCurrentConversation(conversation);
                // On mobile, hide sidebar when conversation is selected
                if (window.innerWidth < 768) {
                    setShowSidebar(false);
                }
            }
        }
    }, [conversationId, conversations, setCurrentConversation]);

    const handleSelectConversation = (id: string) => {
        navigate(`/messages/${id}`);
    };

    const handleBackToList = () => {
        setShowSidebar(true);
        navigate('/messages');
    };

    const handleOpenPersonaPicker = async () => {
        setShowPersonaPicker(true);
        if (personas.length === 0) {
            setPersonasLoading(true);
            setPersonasError(null);
            try {
                const items = await chatbotService.listPersonas();
                setPersonas(items);
            } catch (err) {
                setPersonasError(
                    err instanceof Error ? err.message : 'Failed to load AI chat buddies',
                );
            } finally {
                setPersonasLoading(false);
            }
        }
    };

    const handleStartPersonaChat = async (slug: string) => {
        setStartingPersonaSlug(slug);
        try {
            const { conversationId: newId } = await chatbotService.startConversation(slug);
            await fetchConversations();
            setShowPersonaPicker(false);
            navigate(`/messages/${newId}`);
        } catch (err) {
            setPersonasError(
                err instanceof Error ? err.message : 'Failed to start AI chat',
            );
        } finally {
            setStartingPersonaSlug(null);
        }
    };

    if (authLoading || !user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="messages-page-container">
                {/* Sidebar - Conversation List */}
                <div
                    className={`
                        w-full md:w-96 flex-shrink-0 bg-white border-r border-gray-200
                        ${conversationId && !showSidebar ? 'hidden md:block' : 'block'}
                    `}
                >
                    <div className="h-full flex flex-col">
                        {/* Header */}
                        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
                            <h1 className="text-xl font-semibold text-gray-900">
                                {t('title')}
                            </h1>
                            <button
                                type="button"
                                onClick={handleOpenPersonaPicker}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors"
                            >
                                <svg
                                    className="w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                                </svg>
                                {t('newAiChat', 'New AI chat')}
                            </button>
                        </div>

                        {/* Conversation List */}
                        <div className="flex-1 overflow-y-auto">
                            {loading && conversations.length === 0 ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : error && conversations.length === 0 ? (
                                <div className="p-4 text-center">
                                    <p className="text-red-500">{error}</p>
                                    <button
                                        onClick={() => fetchConversations()}
                                        className="mt-2 text-primary-500 hover:text-primary-600"
                                    >
                                        {t('common:retry')}
                                    </button>
                                </div>
                            ) : conversations.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                                    <svg
                                        className="w-16 h-16 text-gray-300 mb-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                        />
                                    </svg>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        {t('noConversations')}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {t('empty.description')}
                                    </p>
                                </div>
                            ) : (
                                <ConversationList
                                    onSelectConversation={handleSelectConversation}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Content - Chat Room */}
                <div
                    className={`
                        flex-1 flex flex-col min-h-0
                        ${!conversationId || showSidebar ? 'hidden md:flex' : 'flex'}
                    `}
                >
                    {conversationId && currentConversation ? (
                        <>
                            {/* Mobile Back Button */}
                            <div className="md:hidden px-4 py-2 bg-white border-b border-gray-200">
                                <button
                                    onClick={handleBackToList}
                                    className="flex items-center gap-2 text-primary-500"
                                >
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 19l-7-7 7-7"
                                        />
                                    </svg>
                                    {t('common:back')}
                                </button>
                            </div>

                            {/* Chat Room */}
                            <ChatRoom
                                conversationId={conversationId}
                                currentUserId={user.id}
                            />
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center bg-gray-50">
                            <div className="text-center">
                                <svg
                                    className="mx-auto w-16 h-16 text-gray-300 mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                    />
                                </svg>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {t('selectConversation')}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {t('selectConversationHint')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

            {/* AI Chatbot Persona Picker Modal */}
            {showPersonaPicker && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                    onClick={() => setShowPersonaPicker(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {t('choosePersona', 'Choose an AI chat buddy')}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowPersonaPicker(false)}
                                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                                aria-label={t('close', 'Close')}
                            >
                                ×
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {personasLoading ? (
                                <div className="flex justify-center py-8">
                                    <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : personasError ? (
                                <p className="text-center text-red-500 py-4">{personasError}</p>
                            ) : personas.length === 0 ? (
                                <p className="text-center text-gray-500 py-4">
                                    {t('noPersonas', 'No AI chat buddies available yet.')}
                                </p>
                            ) : (
                                personas.map((p) => (
                                    <button
                                        key={p.slug}
                                        type="button"
                                        disabled={startingPersonaSlug !== null}
                                        onClick={() => handleStartPersonaChat(p.slug)}
                                        className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-primary-400 hover:bg-primary-50 transition-colors disabled:opacity-50"
                                    >
                                        <div className="flex items-center gap-3">
                                            {p.avatarUrl ? (
                                                <img
                                                    src={p.avatarUrl}
                                                    alt={p.name}
                                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-primary-600 font-semibold text-sm">
                                                        {p.name.charAt(0)}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-gray-900">{p.name}</h3>
                                                <p className="text-sm text-gray-500 mt-0.5">{t(`persona.${p.slug}.tagline`, p.tagline)}</p>
                                            </div>
                                        </div>
                                        {startingPersonaSlug === p.slug && (
                                            <p className="text-xs text-primary-500 mt-2 ml-13">
                                                {t('starting', 'Starting chat...')}
                                            </p>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessagesPage;
